import { useGLTF } from "@react-three/drei";
import { useMemo, useEffect } from "react";
import * as THREE from "three";
import { DoubleSide } from "three";
import GlbLoader from "../../Utils/GlbLoader";
import { assignMaterialToObjects } from "../../Utils/AssignMaterialToObject";
import { stateMemory, stateContentReady } from "../../store";
import { useSnapshot } from "valtio";

export default function MenschenRoomShell({ visible }) {
  const { ParkettColor, ParkettNormal, ParkettRough, imperfectionRoughMap } = GlbLoader();
  const snapStateMemory = useSnapshot(stateMemory);
  const contentReady = useSnapshot(stateContentReady)[5];
  const gltf = useGLTF("../models/Museum01/Menschen.glb");

  const mats = useMemo(() => {
    ParkettColor.repeat.set(3, 3);
    ParkettNormal.repeat.set(3, 3);
    ParkettRough.repeat.set(3, 3);

    const m = {
      blackGlasMat:      new THREE.MeshStandardMaterial({ color: 0x000000, roughness: 0.1, transparent: true, opacity: 0.85, side: DoubleSide }),
      granitWallMat:     new THREE.MeshStandardMaterial({ roughness: 0.8, metalness: 0 }),
      granitFloorMat:    new THREE.MeshStandardMaterial({ roughness: 0.8, metalness: 0 }),
      parkettMat:        new THREE.MeshStandardMaterial({ roughness: 0.8, metalness: 0, map: ParkettColor, roughnessMap: ParkettRough, normalMap: ParkettNormal }),
      blackMattMat:      new THREE.MeshStandardMaterial({ roughness: 0.8, color: "#181818", roughnessMap: imperfectionRoughMap }),
      blackMattFloorMat: new THREE.MeshStandardMaterial({ roughness: 0.8, color: "#262626", roughnessMap: imperfectionRoughMap }),
      blackMattWallMat:  new THREE.MeshStandardMaterial({ roughness: 0.8, color: "#262626", roughnessMap: imperfectionRoughMap }),
      whiteMattWallMat:  new THREE.MeshStandardMaterial({ roughness: 0.7, color: "white" }),
      blackGlossyMat:    new THREE.MeshStandardMaterial({ roughness: 0.1, color: "#181818", metalness: 0, roughnessMap: imperfectionRoughMap }),
      cleanWoodMat:      new THREE.MeshStandardMaterial({ roughness: 1, metalness: 0 }),
      orangeGlossyWall:  new THREE.MeshStandardMaterial({ roughness: 0, metalness: 0, color: "#fc8207" }),
    };

    assignMaterialToObjects(gltf.scene, ["glas"], m.blackGlasMat);
    assignMaterialToObjects(gltf.scene, ["WallMattBleck"], m.granitWallMat);
    assignMaterialToObjects(gltf.scene, ["Stein_Granite"], m.granitFloorMat);
    assignMaterialToObjects(gltf.scene, ["Parkett"], m.parkettMat);
    assignMaterialToObjects(gltf.scene, ["Arrows", "Box415", "dach"], m.blackMattMat);
    assignMaterialToObjects(gltf.scene, ["Line001"], m.blackMattWallMat);
    assignMaterialToObjects(gltf.scene, ["Floor_Black", "SchilderBlack"], m.blackMattFloorMat);
    assignMaterialToObjects(gltf.scene, ["SchilderOrange"], m.orangeGlossyWall);
    assignMaterialToObjects(gltf.scene, ["Glossy_Black"], m.blackGlossyMat);
    assignMaterialToObjects(gltf.scene, ["WaenbdeWhite"], m.whiteMattWallMat);
    assignMaterialToObjects(gltf.scene, ["Floor_Mat"], m.cleanWoodMat);

    return m;
  }, [gltf.scene]);

  useEffect(() => {
    if (!visible || !contentReady) return;

    const base = snapStateMemory.memoryLowDevice
      ? "../overallTextures/Menschen/LOW/"
      : "../overallTextures/Menschen/";

    let active = true;
    const loadedTextures = [];

    const track = (tex) => { loadedTextures.push(tex); return tex; };

    const loader = new THREE.TextureLoader();

    loader.load(base + "GraniteWallColor.webp", (tex) => {
      if (!active) { tex.dispose(); return; }
      tex.flipY = false; tex.colorSpace = THREE.SRGBColorSpace;
      tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
      track(tex);
      mats.granitWallMat.map = tex; mats.granitWallMat.needsUpdate = true;
      mats.granitFloorMat.map = tex; mats.granitFloorMat.needsUpdate = true;
    });
    loader.load(base + "GraniteWallNormal.webp", (tex) => {
      if (!active) { tex.dispose(); return; }
      tex.flipY = false; tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
      track(tex);
      mats.granitWallMat.normalMap = tex; mats.granitWallMat.needsUpdate = true;
      mats.granitFloorMat.normalMap = tex; mats.granitFloorMat.needsUpdate = true;
    });
    loader.load(base + "GraniteWallRough.webp", (tex) => {
      if (!active) { tex.dispose(); return; }
      tex.flipY = false; tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
      track(tex);
      mats.granitWallMat.roughnessMap = tex; mats.granitWallMat.needsUpdate = true;
      mats.granitFloorMat.roughnessMap = tex; mats.granitFloorMat.needsUpdate = true;
    });
    loader.load(base + "CleanWoodColor.webp", (tex) => {
      if (!active) { tex.dispose(); return; }
      tex.flipY = false; tex.colorSpace = THREE.SRGBColorSpace;
      tex.wrapS = tex.wrapT = THREE.RepeatWrapping; tex.repeat.set(1.5, 1.5);
      track(tex);
      mats.cleanWoodMat.map = tex; mats.cleanWoodMat.needsUpdate = true;
    });
    loader.load(base + "CleanWoodNormal.webp", (tex) => {
      if (!active) { tex.dispose(); return; }
      tex.flipY = false; tex.wrapS = tex.wrapT = THREE.RepeatWrapping; tex.repeat.set(1.5, 1.5);
      track(tex);
      mats.cleanWoodMat.normalMap = tex; mats.cleanWoodMat.needsUpdate = true;
    });
    loader.load(base + "CleanWoodRough.webp", (tex) => {
      if (!active) { tex.dispose(); return; }
      tex.flipY = false; tex.wrapS = tex.wrapT = THREE.RepeatWrapping; tex.repeat.set(1.5, 1.5);
      track(tex);
      mats.cleanWoodMat.roughnessMap = tex; mats.cleanWoodMat.needsUpdate = true;
    });
    loader.load(base + "BakeMenschenWallAO.webp", (tex) => {
      if (!active) { tex.dispose(); return; }
      tex.flipY = false; tex.colorSpace = THREE.SRGBColorSpace; tex.channel = 1;
      track(tex);
      [mats.blackMattWallMat, mats.whiteMattWallMat, mats.granitWallMat].forEach(m => {
        m.aoMap = tex; m.aoMapIntensity = 0.8; m.needsUpdate = true;
      });
    });
    loader.load(base + "BakeMeschenFloorAO.webp", (tex) => {
      if (!active) { tex.dispose(); return; }
      tex.flipY = false; tex.colorSpace = THREE.SRGBColorSpace; tex.channel = 1;
      track(tex);
      [mats.cleanWoodMat, mats.blackMattFloorMat, mats.blackGlossyMat, mats.granitFloorMat, mats.parkettMat].forEach(m => {
        m.aoMap = tex; m.aoMapIntensity = 0.8; m.needsUpdate = true;
      });
    });

    return () => {
      active = false;
      loadedTextures.forEach(tex => tex.dispose());
      mats.granitWallMat.map = null; mats.granitWallMat.normalMap = null; mats.granitWallMat.roughnessMap = null;
      mats.granitFloorMat.map = null; mats.granitFloorMat.normalMap = null; mats.granitFloorMat.roughnessMap = null;
      mats.cleanWoodMat.map = null; mats.cleanWoodMat.normalMap = null; mats.cleanWoodMat.roughnessMap = null;
      [mats.blackMattWallMat, mats.whiteMattWallMat, mats.granitWallMat,
        mats.cleanWoodMat, mats.blackMattFloorMat, mats.blackGlossyMat, mats.granitFloorMat, mats.parkettMat].forEach(m => {
        m.aoMap = null; m.needsUpdate = true;
      });
    };
  }, [visible, contentReady]);

  return <primitive object={gltf.scene} visible={visible} />;
}
